import { useRef } from "react";

/**
 * Lightweight cancel-token for async effects.
 *
 * Consumers check `token.cancelled` between async steps to bail out of stale
 * sequences. The `cancel()` method flips the flag and fires any registered
 * callbacks (used by BatchOrchestrator to skip processing in-flight results).
 */
export interface AbortToken {
  /** True once `cancel()` has been called. Check between awaits. */
  readonly cancelled: boolean;
  /** Human-readable reason for the cancellation (e.g. "view switched"). */
  readonly reason: string | undefined;
  /** Imperatively cancel this token. Safe to call multiple times. */
  cancel(reason?: string): void;
  /** Register a callback that fires once when the token is cancelled. */
  onCancel(cb: () => void): void;
}

/** Thrown by helpers that translate a token cancellation into a catchable error. */
export class AbortError extends Error {
  constructor(reason?: string) {
    super(reason ?? "Operation cancelled");
    this.name = "AbortError";
  }
}

/** Returns true when `err` is an AbortError (no instanceof check across bundles). */
export function isAbortError(err: unknown): err is AbortError {
  return err instanceof Error && err.name === "AbortError";
}

function createAbortToken(): AbortToken {
  let cancelled = false;
  let reason: string | undefined;
  const listeners: Array<() => void> = [];

  return {
    get cancelled() {
      return cancelled;
    },
    get reason() {
      return reason;
    },
    cancel(r?: string) {
      if (cancelled) return;
      cancelled = true;
      reason = r;
      for (const cb of listeners) cb();
      listeners.length = 0;
    },
    onCancel(cb: () => void) {
      if (cancelled) {
        cb();
        return;
      }
      listeners.push(cb);
    },
  };
}

/**
 * Lightweight cancel-token helper for async effects.
 * Call `newToken()` at the start of each async sequence — it automatically
 * cancels the previous token and returns a fresh one. Guard async continuations
 * with `if (token.cancelled) return` to abort stale responses.
 */
export function useAbortToken() {
  const abortRef = useRef(createAbortToken());

  function newToken(reason?: string): AbortToken {
    abortRef.current.cancel(reason ?? "superseded");
    const token = createAbortToken();
    abortRef.current = token;
    return token;
  }

  return { newToken };
}
